import { Calendar, Loader2, Settings as SettingsIcon } from 'lucide-react';
import { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useScheduleEvents } from '../../hooks/schedule/useScheduleEvents';
import { useMyEnrollments } from '../../hooks/schedule/useMyEnrollments';
import {
  applyScheduleFilters,
  deriveFilterOptions,
  useScheduleFilters,
} from '../../hooks/schedule/useScheduleFilters';

import { WeekCalendar } from './components/WeekCalendar';
import { DayCalendar } from './components/DayCalendar';
import { MonthCalendar } from './components/MonthCalendar';
import { ScheduleToolbar } from './components/ScheduleToolbar';
import { ScheduleFilterDrawer } from './components/ScheduleFilterDrawer';
import { ClassDetailsModal } from './components/ClassDetailsModal';
import { CreateEditClassModal } from './components/CreateEditClassModal';
import {
  rangeForView,
  startOfDay,
  stepAnchor,
  toDateKey,
  type ScheduleView,
} from './components/scheduleDateUtils';
import type { ScheduleEvent } from '../../services/schedule';

export default function SchedulePage() {
  const { role, isLoading: companyLoading } = useCurrentCompany();
  const isOwner = role === 'owner' || role === 'admin';

  const [view, setView] = useState<ScheduleView>('week');
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(new Date()));
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [selectedEvent, setSelectedEvent] = useState<ScheduleEvent | null>(null);
  // undefined = modal closed, null = creating new, ScheduleEvent = editing
  const [editingEvent, setEditingEvent] = useState<ScheduleEvent | null | undefined>(undefined);
  const [createDefaultDate, setCreateDefaultDate] = useState<string | undefined>(undefined);

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => rangeForView(view, anchor),
    [view, anchor]
  );

  const { events, isLoading: eventsLoading } = useScheduleEvents(rangeStart, rangeEnd);
  const { enrollments, enrolledClassIds } = useMyEnrollments();

  const {
    filters,
    toggleArrayFilter,
    setAgeBracket,
    clearFilters,
    activeCount,
  } = useScheduleFilters();

  // Filter options derived from the unfiltered events in the current range so
  // users can always toggle every value they could pick.
  const filterOptions = useMemo(() => deriveFilterOptions(events), [events]);

  // Group filtered events by date (YYYY-MM-DD) for the view components.
  const eventsByDate = useMemo(() => {
    const filtered = applyScheduleFilters(events, filters);
    return filtered.reduce<Record<string, ScheduleEvent[]>>((acc, event) => {
      const date = event.start_time?.split('T')[0];
      if (date) {
        if (!acc[date]) acc[date] = [];
        acc[date].push(event);
      }
      return acc;
    }, {});
  }, [events, filters]);

  const checkedInClassIds = useMemo(
    () => new Set(enrollments.filter((e) => e.attended).map((e) => e.class_id)),
    [enrollments]
  );

  const handlePrev = useCallback(() => {
    setAnchor((prev) => stepAnchor(view, prev, -1));
  }, [view]);

  const handleNext = useCallback(() => {
    setAnchor((prev) => stepAnchor(view, prev, 1));
  }, [view]);

  const handleToday = useCallback(() => setAnchor(startOfDay(new Date())), []);

  const handleViewChange = useCallback((next: ScheduleView) => {
    setView(next);
  }, []);

  const handleEventClick = useCallback((event: ScheduleEvent) => setSelectedEvent(event), []);

  const handleCreateClass = useCallback((date: string) => {
    setCreateDefaultDate(date);
    setEditingEvent(null);
  }, []);

  const handleCreateClassFromToolbar = useCallback(() => {
    // Use the anchor date for day/month and today for week — matches the
    // previous behavior where the toolbar's "New Class" used today's date.
    const date = view === 'day' ? toDateKey(anchor) : toDateKey(new Date());
    handleCreateClass(date);
  }, [view, anchor, handleCreateClass]);

  const handleEditClass = useCallback(() => {
    if (!selectedEvent) return;
    setEditingEvent(selectedEvent);
    setSelectedEvent(null);
  }, [selectedEvent]);

  const handleCloseDetails = useCallback(() => setSelectedEvent(null), []);
  const handleCloseCreateEdit = useCallback(() => {
    setEditingEvent(undefined);
    setCreateDefaultDate(undefined);
  }, []);

  // From month view, clicking a day drills into that day's view.
  const handleMonthDayClick = useCallback((date: Date) => {
    setAnchor(startOfDay(date));
    setView('day');
  }, []);

  if (companyLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#A9A3A3]" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Page header */}
      <div className="border-b border-white/10 bg-[#111010] pb-3 md:pb-4 shrink-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Calendar size={22} className="text-rollops-accent" />
            <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold">
              Schedule
            </h1>
          </div>
          {isOwner && (
            <Link
              to="/schedule/settings"
              className="flex items-center gap-1.5 text-xs text-[#A9A3A3] hover:text-[#EBFFEE] bg-[#363232] hover:bg-[#504A4A] px-3 py-1.5 rounded-lg transition-colors"
            >
              <SettingsIcon size={14} />
              Settings
            </Link>
          )}
        </div>
        <p className="text-white/60 mt-1 text-sm">
          {isOwner
            ? 'Manage classes, enrollments, and check-ins'
            : 'Browse and register for classes'}
        </p>
      </div>

      {/* Calendar */}
      <div className="flex-1 bg-[#111010] overflow-auto min-h-0 py-4 md:py-6 flex flex-col">
        <ScheduleToolbar
          view={view}
          anchor={anchor}
          activeFilterCount={activeCount}
          isOwner={isOwner}
          onViewChange={handleViewChange}
          onPrev={handlePrev}
          onNext={handleNext}
          onToday={handleToday}
          onOpenFilters={() => setFilterDrawerOpen(true)}
          onCreateClass={isOwner ? handleCreateClassFromToolbar : undefined}
        />

        <div className="flex-1 min-h-0">
          {view === 'day' && (
            <DayCalendar
              anchor={anchor}
              eventsByDate={eventsByDate}
              enrolledClassIds={enrolledClassIds}
              checkedInClassIds={checkedInClassIds}
              isLoading={eventsLoading}
              onEventClick={handleEventClick}
            />
          )}
          {view === 'week' && (
            <WeekCalendar
              anchor={anchor}
              eventsByDate={eventsByDate}
              enrolledClassIds={enrolledClassIds}
              checkedInClassIds={checkedInClassIds}
              isOwner={isOwner}
              isLoading={eventsLoading}
              onEventClick={handleEventClick}
              onCreateClass={isOwner ? handleCreateClass : undefined}
            />
          )}
          {view === 'month' && (
            <MonthCalendar
              anchor={anchor}
              eventsByDate={eventsByDate}
              enrolledClassIds={enrolledClassIds}
              isOwner={isOwner}
              isLoading={eventsLoading}
              onEventClick={handleEventClick}
              onDayClick={handleMonthDayClick}
              onCreateClass={isOwner ? handleCreateClass : undefined}
            />
          )}
        </div>
      </div>

      {/* Filter drawer */}
      <ScheduleFilterDrawer
        open={filterDrawerOpen}
        filters={filters}
        options={filterOptions}
        activeCount={activeCount}
        onClose={() => setFilterDrawerOpen(false)}
        onToggle={toggleArrayFilter}
        onSetAgeBracket={setAgeBracket}
        onClear={clearFilters}
      />

      {/* Class details modal */}
      {selectedEvent && (
        <ClassDetailsModal
          event={selectedEvent}
          isOwner={isOwner}
          onClose={handleCloseDetails}
          onEdit={isOwner ? handleEditClass : undefined}
        />
      )}

      {/* Create / edit modal */}
      {editingEvent !== undefined && (
        <CreateEditClassModal
          event={editingEvent}
          defaultDate={createDefaultDate}
          onClose={handleCloseCreateEdit}
          onDeleted={handleCloseCreateEdit}
        />
      )}
    </div>
  );
}
